import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft } from "react-icons/bs";
import { fire, rewardFrame, tradeSFrame } from "../../../assets/images";
import useAuthAxios from "../../../utils/baseAxios";
import { API } from "../../../constants/api";
import { referralLinkFor } from "../../../constants/links";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { errorMessage } from "../../../utils/errorMessage";
import Activity from "./Activity";
import Referral from "../../settings/Referral";
import { getDeviceInfo } from "../../../utils/deviceInfo";
import { useUser } from "../../../context/user-context";
import CopyToClipboard from "../../../components/utils/CopyToClipboard";
import { GoChevronRight } from "react-icons/go";
import ClaimModal from "../ClaimModal";
import { FaCheck } from "react-icons/fa";
import StreakModal from "../StreakModal";
import CashbackClaim from "./CashbackClaim";
import { ClipLoader } from "react-spinners";

type Slide = {
  key: string;
  title: string;
  amount: number;
  progressPercent: number;
  trailingText: string;
  canClaim: boolean;
  timeLeft?: string;
  claimAmount: number;
  targetAmount: number;
};

const CashbackSlider = ({
  cashbacks,
  onClaim,
  setShowClaimModalCashback,
  onSetClaimAmount,
}: any) => {
  const queryClient = useQueryClient();
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const axiosInstance = useAuthAxios();
  const [loadingIndex, setLoadingIndex] = useState<string | null>(null);
  const records = Array.isArray(cashbacks?.data) ? cashbacks?.data : [];
  const formatRemaining = (startedAt?: string, durationDays?: number) => {
    if (!startedAt || !durationDays) return "";
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return "";
    const end = start + durationDays * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let diff = end - now;
    if (diff <= 0) return "0 days : 00 Hours : 00 Mins";
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    diff -= days * 24 * 60 * 60 * 1000;
    const hours = Math.floor(diff / (60 * 60 * 1000));
    diff -= hours * 60 * 60 * 1000;
    const mins = Math.floor(diff / (60 * 1000));
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${days} days : ${pad(hours)} Hours : ${pad(mins)} Mins`;
  };
  const slides: Slide[] = records.map((item: any, idx: number) => {
    const duration = (
      item?.duration ||
      item?.adminCashbackId?.duration ||
      ""
    ).toString();
    const durationLabel = duration
      ? duration.charAt(0).toUpperCase() + duration.slice(1)
      : "Cashback";
    const currentAmount = Number(item?.currentAmount ?? 0);
    const targetAmount = Number(
      item?.targetAmount ?? item?.adminCashbackId?.targetAmount ?? 0
    );
    const cashbackAmount = Number(
      item?.cashbackAmount ?? item?.adminCashbackId?.cashbackAmount ?? 0
    );
    const progressPercent = targetAmount
      ? Math.min(100, Math.round((currentAmount / targetAmount) * 100))
      : 0;
    const claimAmount = Number((progressPercent / 100) * cashbackAmount);
    const durationDays =
      duration === "weekly"
        ? 7
        : duration === "monthly"
        ? 30
        : duration === "yearly"
        ? 365
        : undefined;
    const timeLeft = formatRemaining(item?.startedAt, durationDays);
    return {
      key: item?._id ?? `${duration}-${idx}`,
      title: `${durationLabel} Cashback`,
      amount: cashbackAmount,
      progressPercent,
      trailingText: `₦${currentAmount.toLocaleString()}`,
      canClaim: Boolean(item?.canClaim),
      timeLeft,
      claimAmount,
      targetAmount,
    };
  });

  const claimCashback = async (data: any) => {
    const response = await axiosInstance.post(API.claimCashback(data.key), {
      data,
    });
    return response.data;
  };

  const completeClaimCashback = useMutation({
    mutationFn: claimCashback,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["get-point-activity"] });
      queryClient.invalidateQueries({ queryKey: ["get-cashbacks"] });
      toast.success(r.message);
      setLoadingIndex(null);
      setTimeout(() => {
        setShowClaimModalCashback(true);
      }, 1500);
    },
    onError: (e) => {
      // console.log(e);
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
      setLoadingIndex(null);
    },
  });

  return (
    <div className="w-full">
      <div className="w-full overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide: Slide) => {
            return (
              <div key={slide.key} className="w-full shrink-0 px-4">
                <div className="w-full  flex justify-between items-center ">
                  <h4 className="text-gray-400 dark:text-gray-300 text-[14px] font-medium ">
                    {slide.title}
                  </h4>
                  {slide.canClaim ? (
                    <button
                      onClick={() => {
                        setLoadingIndex(slide.key);
                        if (onSetClaimAmount)
                          onSetClaimAmount(slide.claimAmount);
                        completeClaimCashback.mutate({ key: slide.key });
                      }}
                      className="flex items-center justify-center text-white bg-secondary_button font-semibold text-[12px] px-4 py-1 rounded-[10px] "
                    >
                      {loadingIndex === slide.key ? (
                        <ClipLoader color="#FFFFFF" size={15} />
                      ) : (
                        "Claim"
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        toast.error(
                          "You are not eligible to claim this cashback yet"
                        );
                      }}
                      className="flex items-center "
                    >
                      <h4 className="text-[20px] dark:text-gray-100 text-gray-900 font-bold">
                        ₦{slide.amount.toLocaleString()}
                      </h4>
                      <GoChevronRight className="text-gray-500 dark:text-gray-400 text-[24px]" />
                    </button>
                  )}
                </div>
                <div className="w-full flex items-center mt-2">
                  <div className="flex-1 h-[4px] rounded-full bg-[#13181B] dark:bg-gray-800 overflow-hidden">
                    <div
                      className="h-[4px] bg-[#0494FC]"
                      style={{ width: `${slide.progressPercent}%` }}
                    />
                  </div>
                  <span className="ml-3 text-[#1A1A1A] dark:text-gray-300 text-[12px] ">
                    {slide.trailingText}/
                    {"₦" + (slide?.targetAmount ?? 0).toLocaleString()}
                  </span>
                </div>
                <h4 className="text-gray-500 mt-2 text-[12px] font-semibold ">
                  {slide.timeLeft}
                </h4>
              </div>
            );
          })}
        </div>
      </div>
      <div className="w-full flex justify-center items-center gap-2 mt-3">
        {slides.map((_: Slide, idx: number) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`w-[8px] h-[8px] rounded-full ${
              currentSlide === idx ? "bg-[#0494FC]" : "bg-[#C7C7C7]"
            }`}
            aria-label={`Go to ${slides[idx].title}`}
          />
        ))}
      </div>
    </div>
  );
};
const Rewards = ({ setOpenRewards, setRedeemModal }: any) => {
  const navigate = useNavigate();
  const axiosInstance = useAuthAxios();
  const [showClaimModal, setShowClaimModal] = useState<boolean>(false);
  const [showClaimModalCashback, setShowClaimModalCashback] =
    useState<boolean>(false);
  const [cashbackClaimAmount, setCashbackClaimAmount] = useState<number | null>(
    null
  );
  const [disabled, setDisabled] = useState<boolean>(true);
  const [openRefer, setOpenRefer] = useState<boolean>(false);
  const [showStreakModal, setShowStreakModal] = useState<boolean>(false);
  const getPoint = async () => {
    const response = await axiosInstance.get(API.getPointsBalance);
    return response.data;
  };
  const { data: point, error: error1 } = useQuery({
    queryKey: ["get-point"],
    queryFn: getPoint,
    retry: 1,
  });
  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);
  const pointBalance = point?.data?.totalPoints;
  useEffect(() => {
    if (pointBalance < 200) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [pointBalance]);

  const getStreak = async () => {
    const response = await axiosInstance.get(API.getStreak);
    return response.data;
  };
  const { data: streakDetails, error: error4 } = useQuery({
    queryKey: ["get-streak"],
    queryFn: getStreak,
    retry: 1,
  });
  // console.log(streakDetails?.data);
  useEffect(() => {
    if (error4) {
      const newError = error4 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error4]);

  // const newPoint = Math.round((pointBalance / 200) * 100);

  const { userDetails } = useUser();
  const referralcode = userDetails?.user.profile.username
    ? userDetails?.user.profile.username
    : userDetails?.user.referralCode;
  const referralLink = referralLinkFor(referralcode);

  const deviceInfo = getDeviceInfo();

  const trackReferral = async (data: any) => {
    const response = await axiosInstance.post(API.trackEvent, data);
    return response.data;
  };

  // console.log(deviceInfo);
  const completeTrackReferral = useMutation({
    mutationFn: trackReferral,
    onSuccess: (r: any) => {
      // toast.success(r.message);
    },
    onError: (e: any) => {
      // console.log(e);
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  const handleTrackReferral = () => {
    const data = {
      event: "REFERRAL__REFERRAL_LINK_COPIED",
      properties: {
        ip: "0.0.0.0",
        platform: "Web",
        deviceInfo: {
          name: deviceInfo.deviceName,
          uniqueId: deviceInfo.uniqueId,
          os: deviceInfo.operatingSystem?.family,
        },
      },
    };
    completeTrackReferral.mutate(data);
  };

  const getSignupStats = async () => {
    const response = await axiosInstance.get(API.getSignupStats);
    return response.data;
  };
  const { data: signupStats, error: error2 } = useQuery({
    queryKey: ["get-signup-stats"],
    queryFn: getSignupStats,
    retry: 1,
  });
  useEffect(() => {
    if (error2) {
      const newError = error2 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error2]);
  const getCashbacks = async () => {
    const response = await axiosInstance.get(API.getCashbacks);
    return response.data;
  };
  const { data: cashbacks, error: error3 } = useQuery({
    queryKey: ["get-cashbacks"],
    queryFn: getCashbacks,
    retry: 1,
  });
  useEffect(() => {
    if (error3) {
      const newError = error3 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error3]);
  const {
    kycCompleted,
    madeMinAmountTrade,
    referredUserMadeCryptoTransaction,
    eligible,
  } = signupStats?.data?.status || {};
  const statusList = [
    kycCompleted,
    madeMinAmountTrade,
    referredUserMadeCryptoTransaction,
  ];
  const completedCount = statusList.filter(Boolean).length;
  const totalCount = statusList.length;

  const streak = streakDetails?.data;
  const weeklyProgress = streak?.weeklyProgress || {};
  // console.log(cashbacks);

  // Get the current day of the week
  const currentDay = new Date().toLocaleString("en-US", { weekday: "short" });

  return (
    <div className=" w-full mt-10 lgss:mt-0 font-manrope">
      <div className="w-full  xxl:px-12 xxxl:px-16 md:px-12 mds:px-8 px-4 flex flex-col  gap-6">
        <div className="w-full flex justify-start gap-2 items-center">
          <button
            onClick={() => {
              setOpenRewards(false);
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[18px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[15px]">
            Rewards
          </h4>
        </div>
        <div className="w-full flex flex-col lgss:flex-row gap-10">
          <div className="w-full lgss:w-3/5 p-4  bg-white dark:bg-[#1C2227] rounded-[24px]">
            <div className="w-full">
              <img src={rewardFrame} alt="" className="w-full bg-cover " />
            </div>
            <div className="w-full  mt-6 gap-4  flex flex-col justify-center items-center  ">
              <h4 className="text-gray-500 text-[13px] ">Your referral link</h4>
              <div className="h-[58px] gap-4  border-gray-300 border text-gray-900 dark:text-white text-[14px] text-center dark:border-gray-700 flex justify-center items-center px-4  rounded-[8px] ">
                {referralLink}
              </div>
              <CopyToClipboard text={referralLink} onCopy={handleTrackReferral}>
                <h4 className="text-secondary_button  text-[16px] font-medium">
                  COPY LINK
                </h4>
              </CopyToClipboard>
            </div>
            <div className="w-full mt-10 border-b-8 border-gray-200 dark:border-gray-700 pb-4">
              <h4 className="text-gray-900 dark:text-gray-100 text-[14px] font-bold ">
                Explore
              </h4>
              <div className="w-full grid grid-cols-2 mt-4 gap-2 sm:gap-4 grid-rows-1">
                <div
                  onClick={() => setShowStreakModal(true)}
                  className="w-full min-h-[120px] sm:min-h-[140px] h-full rounded-xl overflow-hidden cursor-pointer"
                >
                  <img
                    src={tradeSFrame}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  onClick={() => setOpenRefer(true)}
                  className="w-full min-h-[120px] sm:min-h-[140px] h-full bg-referralBg bg-cover cursor-pointer bg-right bg-no-repeat p-4 sm:p-6 rounded-xl flex flex-col justify-end"
                >
                  <div className="w-full">
                    <h3 className="font-sora font-semibold text-[#2DB7A0] text-[18px] sm:text-[22px] md:text-[26px] leading-tight">
                      Referral
                      <br />
                      and Earn!
                    </h3>
                    <p className="text-gray-400 text-[10px] sm:text-[11px] w-full mt-1.5 sm:mt-2">
                      Invite your friends and earn points for each friend who
                      signs up.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full mt-6 border-b-8 border-gray-200 dark:border-gray-700 pb-4">
              <CashbackSlider
                cashbacks={cashbacks}
                onClaim={() => setShowClaimModalCashback(true)}
                setShowClaimModalCashback={setShowClaimModalCashback}
                onSetClaimAmount={(amt: number) => setCashbackClaimAmount(amt)}
              />
            </div>

            <div className="w-full mt-6 border-b-8  border-gray-200 dark:border-gray-700 pb-4">
              <div className="w-full  flex justify-between items-center px-4">
                <div>
                  <h4 className="text-gray-400 dark:text-gray-300 text-[14px] ">
                    DeeXpoints
                  </h4>
                  <h4 className="text-gray-500 mt-2 text-[12px] font-semibold ">
                    ₦{pointBalance * 10} available to withdraw
                  </h4>
                </div>
                <button
                  disabled={disabled}
                  onClick={() => setRedeemModal(true)}
                  className="flex items-center "
                >
                  <h4 className="text-[20px] dark:text-gray-100 text-gray-900 font-bold">
                    {pointBalance}/200
                  </h4>
                  <GoChevronRight className="text-gray-500 dark:text-gray-400 text-[24px]" />
                </button>
              </div>
            </div>
            <div
              className={`w-full mt-6  ${
                eligible &&
                "border-b-8 border-gray-200 dark:border-gray-700 pb-4"
              }`}
            >
              <div className="w-full  flex justify-between items-center px-4">
                <div>
                  <h4 className="text-gray-400 dark:text-gray-300 text-[14px]  ">
                    Daily Streak
                  </h4>
                  <div className="flex justify-center mt-4">
                    <div className="flex space-x-2">
                      {Object.entries(weeklyProgress).map(([day, active]) => (
                        <div
                          key={day}
                          className="flex flex-col items-center gap-1"
                        >
                          <h4
                            className={`text-[11px] ${
                              active && currentDay.includes(day)
                                ? "text-[#D70452]"
                                : "text-[#7F7F7F]"
                            }`}
                          >
                            {day}
                          </h4>
                          <div
                            className={`w-[24px] h-[24px] rounded-full  ${
                              active && currentDay.includes(day)
                                ? "bg-transparent"
                                : active
                                ? "bg-[#095B97]"
                                : "border border-[#D5D5D5]"
                            } flex justify-center items-center`}
                          >
                            {active ? (
                              currentDay.includes(day) ? (
                                <img
                                  src={fire}
                                  alt="Flame Icon"
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <FaCheck className="text-white text-[12px]" />
                              )
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowStreakModal(true)}
                  className="flex items-center "
                >
                  <h4 className="text-[20px] dark:text-gray-100 text-gray-900 font-bold">
                    ₦1000
                  </h4>
                  <GoChevronRight className="text-gray-500 text-[24px]" />
                </button>
              </div>
            </div>
            {eligible && (
              <div className="w-full mt-6 ">
                <div
                  className={`w-full  flex justify-between items-center px-4 ${
                    !eligible ? "opacity-30 " : ""
                  }`}
                >
                  <div>
                    <h4 className="text-gray-400 dark:text-gray-300 text-[14px] ">
                      Sign Up Bonus
                    </h4>
                    <h4 className="text-gray-500 mt-2 text-[12px] font-semibold ">
                      {completedCount}/{totalCount} items completed
                    </h4>
                  </div>
                  <button
                    disabled={!eligible}
                    onClick={() => eligible && setShowClaimModal(true)}
                    className={`flex items-center ${
                      !eligible ? "cursor-not-allowed" : ""
                    }`}
                  >
                    <h4 className="text-[20px] dark:text-gray-100 text-gray-900 font-bold">
                      ₦5000
                    </h4>
                    <GoChevronRight className="text-gray-500 dark:text-gray-400 text-[24px]" />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="w-full lgss:w-2/5   bg-white dark:bg-[#1C2227] rounded-[24px] h-[600px]">
            <Activity />
          </div>
        </div>
      </div>
      {openRefer && <Referral setOpenRefer={setOpenRefer} />}
      {showClaimModal && (
        <ClaimModal
          setShowClaimModal={setShowClaimModal}
          onNavigateKyc={() => {
            setShowClaimModal(false);
            navigate("/kyc-verification");
          }}
          onOpenSellFlow={() => {
            setShowClaimModal(false);
            setOpenRewards(false);
            navigate("/dashboard", {
              replace: true,
              state: { openSellFromClaim: true },
            });
          }}
          onOpenRewardsStep={() => {
            setShowClaimModal(false);
            setOpenRefer(true);
          }}
        />
      )}
      {showClaimModalCashback && (
        <CashbackClaim
          setShowClaimModalCashback={setShowClaimModalCashback}
          claimAmount={cashbackClaimAmount}
        />
      )}
      {showStreakModal && (
        <StreakModal
          onClose={() => setShowStreakModal(false)}
          streakDetails={streakDetails}
        />
      )}
    </div>
  );
};

export default Rewards;
